import {
  IComparisonChecksPage,
  IComparisonChecksRequest,
  IComparisonClusterDetail,
  IComparisonFilters,
  IComparisonSummary,
  IRecordVerdictRequest,
  IReviewQueue,
  IVerdictAccuracy,
  IVerifiedFilters,
  IVerifiedStatus,
} from "types/BenefitComparison/BenefitComparison";

import { api } from ".";

const toParams = (filter: object) =>
  Object.fromEntries(
    Object.entries(filter).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

/**
 * CLE-2820 PR 3 — the Legacy vs Engine vs AI tab. Read-only, SuperAdmin on the API.
 *
 * CLE-2820 PR 4 adds the one write: recording a human verdict. Every read carries verdict data, so
 * all of them are tagged `BenefitVerdict` and the mutation invalidates the tag — after a verdict the
 * queue, the tiles and the checks all have to move together.
 */
const benefitComparisonQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getBenefitComparisonSummary: builder.query<
      { data: IComparisonSummary; message: string },
      IComparisonFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/summary",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    getBenefitComparisonChecks: builder.query<
      { data: IComparisonChecksPage; message: string },
      IComparisonChecksRequest
    >({
      query: (request) => ({
        url: "benefitComparison/checks",
        params: toParams(request),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    getBenefitComparisonCluster: builder.query<
      { data: IComparisonClusterDetail; message: string },
      IComparisonChecksRequest
    >({
      query: (request) => ({
        url: "benefitComparison/cluster",
        params: toParams(request),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    getBenefitReviewQueue: builder.query<
      { data: IReviewQueue; message: string },
      IComparisonFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/queue",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    getBenefitVerdictAccuracy: builder.query<
      { data: IVerdictAccuracy; message: string },
      IComparisonFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/verdictAccuracy",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    /**
     * CLE-2821 — what today's engine makes of every verified check. Derived by the nightly run, so
     * recording a verdict does not move it until that run; tagged all the same, because a new verdict
     * does change which checks are in the set the next time this is read.
     */
    getBenefitVerifiedStatus: builder.query<
      { data: IVerifiedStatus; message: string },
      IVerifiedFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/verified",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    // 🚨 The reviewer is NOT in the body — the API takes it from the authenticated user.
    recordBenefitVerdict: builder.mutation<
      { data: { id: string }; message: string },
      IRecordVerdictRequest
    >({
      query: (body) => ({
        url: "benefitComparison/verdict",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
  }),
});

export const {
  useGetBenefitComparisonSummaryQuery,
  useGetBenefitComparisonChecksQuery,
  useGetBenefitComparisonClusterQuery,
  useGetBenefitReviewQueueQuery,
  useGetBenefitVerdictAccuracyQuery,
  useGetBenefitVerifiedStatusQuery,
  useRecordBenefitVerdictMutation,
} = benefitComparisonQueries;
